import type { Session } from "@/lib/session";

/**
 * Base URL for talking to the Laravel API. Server-side code (Server
 * Components, ISR) uses the internal Docker network hostname when
 * available; client-side code always uses the browser-accessible URL.
 */
export function apiBaseUrl(): string {
  if (typeof window === "undefined" && process.env.INTERNAL_API_URL) {
    return process.env.INTERNAL_API_URL;
  }

  return process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";
}

export class ApiError extends Error {
  status: number;
  errors?: Record<string, string[]>;

  constructor(status: number, message: string, errors?: Record<string, string[]>) {
    super(message);
    this.status = status;
    this.errors = errors;
  }
}

/**
 * Phase 2A: the shared predicate every server page's `catch` block should
 * use before redirecting on a missing-permission response, rather than
 * each page re-writing `error instanceof ApiError && error.status === 403`
 * by hand.
 */
export function isForbidden(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403;
}

/**
 * Authenticated server-side API call. There's no real tenant subdomain to
 * hit in local dev (see ResolveTenant's X-Tenant-Slug fallback on the API
 * side) -- the request always goes to the bare API host, with the tenant
 * identified by header instead. This only ever runs server-side (Server
 * Components, Server Actions, Route Handlers): the token lives in an
 * httpOnly cookie the browser never sees directly.
 */
export async function apiFetch<T>(path: string, session: Session, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${apiBaseUrl()}${path}`, {
    ...init,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.token}`,
      // Omitted entirely for a Super Admin session (no tenantSlug) --
      // ResolveTenant treats a missing header as "no tenant context,"
      // exactly what the admin API expects.
      ...(session.tenantSlug ? { "X-Tenant-Slug": session.tenantSlug } : {}),
      ...init.headers,
    },
    cache: "no-store",
  });

  const body = await res.json().catch(() => ({}));

  if (!res.ok) {
    const message = typeof body.message === "string" ? body.message : "Something went wrong. Please try again.";
    throw new ApiError(res.status, message, body.errors);
  }

  return body as T;
}

export interface BlogPost {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  body: string;
  meta_title: string | null;
  meta_description: string | null;
  is_featured: boolean;
  list_image_url: string | null;
  detail_image_url: string | null;
  author_name: string | null;
  published_at: string;
}

interface Paginated<T> {
  data: T[];
}

export async function getBlogPosts(params: { perPage?: number; featured?: boolean } = {}): Promise<BlogPost[]> {
  const query = new URLSearchParams();
  if (params.perPage) query.set("per_page", String(params.perPage));
  if (params.featured) query.set("featured", "1");
  const qs = query.toString() ? `?${query.toString()}` : "";
  const res = await fetch(`${apiBaseUrl()}/api/blog-posts${qs}`, {
    next: { revalidate: 3600 },
  });

  if (!res.ok) return [];

  const body: Paginated<BlogPost> = await res.json();
  return body.data;
}

export async function getBlogPost(slug: string): Promise<BlogPost | null> {
  const res = await fetch(`${apiBaseUrl()}/api/blog-posts/${slug}`, {
    next: { revalidate: 3600 },
  });

  if (!res.ok) return null;

  return res.json();
}

export interface MarketingFeature {
  id: number;
  title: string;
  short_description: string;
  full_description: string;
  image_url: string | null;
  sort_order: number;
}

export async function getMarketingFeatures(): Promise<MarketingFeature[]> {
  const res = await fetch(`${apiBaseUrl()}/api/marketing-features`, {
    next: { revalidate: 3600 },
  });

  if (!res.ok) return [];

  const body: Paginated<MarketingFeature> = await res.json();
  return body.data;
}

export interface Faq {
  id: number;
  question: string;
  answer: string;
  sort_order: number;
  is_featured: boolean;
}

export async function getFaqs(params: { featured?: boolean } = {}): Promise<Faq[]> {
  const qs = params.featured ? "?featured=1" : "";
  const res = await fetch(`${apiBaseUrl()}/api/faqs${qs}`, {
    next: { revalidate: 3600 },
  });

  if (!res.ok) return [];

  const body: Paginated<Faq> = await res.json();
  return body.data;
}

export interface PlatformPage {
  id: number;
  slug: string;
  title: string;
  body: string;
  updated_at: string;
}

export async function getPlatformPage(slug: string): Promise<PlatformPage | null> {
  const res = await fetch(`${apiBaseUrl()}/api/pages/${slug}`, {
    next: { revalidate: 3600 },
  });

  if (!res.ok) return null;

  const body: { page: PlatformPage } = await res.json();
  return body.page;
}

export interface CustomerStory {
  id: number;
  quote: string;
  author_name: string;
  author_role: string | null;
  rating: number;
  sort_order: number;
  is_featured: boolean;
  created_at: string;
}

export async function getCustomerStories(params: { featured?: boolean } = {}): Promise<CustomerStory[]> {
  const qs = params.featured ? "?featured=1" : "";
  const res = await fetch(`${apiBaseUrl()}/api/customer-stories${qs}`, {
    next: { revalidate: 3600 },
  });

  if (!res.ok) return [];

  const body: { data: CustomerStory[] } = await res.json();
  return body.data;
}

export interface StorefrontPlan {
  id: number;
  slug: string;
  name: string;
  tagline: string | null;
  price_monthly: number;
  price_yearly: number;
  included_features: string[] | null;
  highlighted: boolean;
  show_price: boolean;
}

export interface StorefrontContact {
  support_email: string | null;
  support_phone: string | null;
  whatsapp_number: string | null;
  social: {
    facebook: string | null;
    x: string | null;
    instagram: string | null;
    linkedin: string | null;
  };
}

export interface Storefront {
  plans: StorefrontPlan[];
  add_ons: unknown[];
  contact: StorefrontContact;
}

export async function getStorefront(): Promise<Storefront> {
  const res = await fetch(`${apiBaseUrl()}/api/storefront`, {
    next: { revalidate: 300 },
  });

  if (!res.ok) {
    return { plans: [], add_ons: [], contact: { support_email: null, support_phone: null, whatsapp_number: null, social: { facebook: null, x: null, instagram: null, linkedin: null } } };
  }

  return res.json();
}

export interface LeadPayload {
  name: string;
  email: string;
  phone: string;
  clinic_name?: string;
  message?: string;
  source: "signup" | "contact" | "demo_request" | "chatbot";
  plan_interest?: string;
  captcha_token: string;
  captcha_answer: string;
}

export interface SubmitLeadResult {
  ok: boolean;
  message: string;
  errors?: Record<string, string[]>;
}

export async function submitLead(payload: LeadPayload): Promise<SubmitLeadResult> {
  const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/leads`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });

  const body = await res.json().catch(() => ({}));

  if (!res.ok) {
    const firstError = body.errors ? Object.values(body.errors)[0] : undefined;
    const message = Array.isArray(firstError) ? firstError[0] : body.message ?? "Something went wrong. Please try again.";
    return { ok: false, message, errors: body.errors };
  }

  return { ok: true, message: body.message ?? "Thanks -- we'll be in touch shortly." };
}

export interface Captcha {
  token: string;
  image: string;
}

export async function getCaptcha(): Promise<Captcha | null> {
  const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/captcha`, { cache: "no-store" });
  if (!res.ok) return null;
  return res.json();
}
